function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.hasNavigationButtons = this.prevButton && this.nextButton;

    if (!this.slider) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    if (this.hasNavigationButtons) {
      this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
      this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
    }
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) {
      this.sliderItemsToShow = Array.from(this.sliderItems);
    }
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    if (!this.sliderItemOffset) {
      this.sliderItemOffset = this.sliderItemsToShow[0].getBoundingClientRect().width;
    }
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = Math.max(1, this.sliderItemsToShow.length - this.slidesPerPage + 1);
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping || !this.hasNavigationButtons) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class ProductItemCodeSuggestions extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    if (!this.dataset.itemCode) return;
    this.initializeSuggestions();
  }

  parseSettings() {
    try {
      return JSON.parse(this.dataset.settings || '{}');
    } catch (error) {
      console.error('Invalid data-settings on product-item-code-suggestions', error);
      return {};
    }
  }

  buildFetchUrl() {
    const settings = this.parseSettings();
    const itemCode = settings.item_code || this.dataset.itemCode;
    if (!itemCode) return '';

    const currentProductId = settings.productId || this.dataset.currentProductId;

    const searchParams = new URLSearchParams();

    // 品番は `itemcode:<品番>` のタグで持っているため、タグ検索で同一品番のみを引く。
    // `itemcode:` を裸で渡すと Shopify が未知のフィールド指定として条件ごと破棄し、
    // 無関係な商品が返るため、tag: で囲んだうえでダブルクォートが必須(ハイフン対策)。
    const itemCodeTag = `itemcode:${String(itemCode).replace(/"/g, '')}`;

    searchParams.set('type', 'product');
    searchParams.set('q', `tag:"${itemCodeTag}"`);
    searchParams.set('view', 'item-code-suggestions');

    if (currentProductId) {
      searchParams.set('current_product_id', currentProductId);
    }

    return `${this.dataset.url}?${searchParams.toString()}`;
  }

  loadSuggestions() {
    fetch(this.buildFetchUrl())
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const suggestions = html.querySelector('product-item-code-suggestions');
        const fetchedContainer = suggestions?.querySelector('.size-container');
        const currentContainer = this.querySelector('.size-container');

        if (fetchedContainer && fetchedContainer.children.length && currentContainer) {
          const existingKeys = new Set();
          const expectedItemCode = this.parseSettings().item_code || this.dataset.itemCode;

          currentContainer.querySelectorAll('[data-size-label]').forEach((child) => {
            const key = `${child.dataset.sizeLabel || ''}|${child.dataset.colorLabel || ''}|${child.dataset.rank || ''}`;
            existingKeys.add(key);
          });

          Array.from(fetchedContainer.children).forEach((child) => {
            // タグ検索は `010ISA11-6811` で `010ISA11-6811-1` のような
            // 枝番付き品番も前方一致で拾うため、品番の完全一致を最終チェックする。
            if (child.dataset.itemCode !== expectedItemCode) {
              return;
            }

            const key = `${child.dataset.sizeLabel || ''}|${child.dataset.colorLabel || ''}|${child.dataset.rank || ''}`;

            if (existingKeys.has(key)) {
              return;
            }

            existingKeys.add(key);
            currentContainer.appendChild(child);
          });
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }

  initializeSuggestions() {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadSuggestions();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }
}

if (!customElements.get('product-item-code-suggestions')) {
  customElements.define('product-item-code-suggestions', ProductItemCodeSuggestions);
}


class ProductRecentlyViewed extends HTMLElement {
  constructor() {
    super();
  }

  _getSearchQueryString() {
    var items = JSON.parse(localStorage.getItem('recentlyViewedProducts') || '[]');
    var productId = Number(this.dataset.product_id)
    if (productId && items.includes(productId)) {
      items.splice(items.indexOf(productId), 1);
    }
    return items.map(function (item) {
      return 'id:' + item;
    }).join(' OR ');
  }

  connectedCallback() {
    const isDrawerRecentlyViewed = this.id === 'drawer-recently-viewed';

    const loadRecentlyViewed = () => {
      var queryString = this._getSearchQueryString();
      if (queryString === '') {
        if (isDrawerRecentlyViewed) {
          toggleHistoryFavoriteDrawerItem();
        }
        return;
      }
      var fetchUrl = "".concat(this.dataset.url, "?section_id=").concat(this.dataset.id, "&type=product&q=").concat(queryString);

      fetch(fetchUrl)
          .then((response) => response.text())
        .then((text) => {
            const html = document.createElement('div');
            html.innerHTML = text;
            const recommendations = html.querySelector('product-recently-viewed');
            if (recommendations && recommendations.innerHTML.trim().length) {
              this.innerHTML = recommendations.innerHTML;
            }
            if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
              this.remove();
            }
            if (html.querySelector('.grid__item')) {
              this.classList.add('product-recently-viewed--loaded');
            }

            // 閲覧順に順序を変更
            const productIds = JSON.parse(localStorage.getItem('recentlyViewedProducts') || '[]').reverse();
            const productsList = this.querySelector('.products-list, [data-product-grid-list]');
            if (productsList) {
              productIds.forEach(id => {
                const productElement = productsList.querySelector(`li[data-pid="${id}"]`);
                if (productElement) {
                  productsList.prepend(productElement);
                }
              });
            }

            if (isDrawerRecentlyViewed) {
              toggleHistoryFavoriteDrawerItem();
            }
          })
          .catch((e) => {
            console.error(e);
            if (isDrawerRecentlyViewed) {
              toggleHistoryFavoriteDrawerItem();
            }
          });
    };

    if (isDrawerRecentlyViewed) {
      loadRecentlyViewed();
      return;
    }

    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      loadRecentlyViewed();
    };

    new IntersectionObserver(handleIntersection.bind(this), { rootMargin: '0px 0px 400px 0px' }).observe(this);
  }
}

customElements.define('product-recently-viewed', ProductRecentlyViewed);

function hasDrawerRecentlyViewedItems() {
  const recentlyViewed = document.querySelector('#drawer-recently-viewed');
  if (!recentlyViewed) return false;
  return !!recentlyViewed.querySelector('.menu-drawer__block-item');
}

function hasDrawerWishlistItems() {
  const wishlistPage = document.querySelector('#wishlist-drawer-block wishlist-page');
  if (!wishlistPage) return false;
  return wishlistPage.querySelectorAll('wishlist-product-card').length > 0;
}

function toggleHistoryFavoriteDrawerItem() {
  const historyFavoriteItem = document.querySelector('#history-favorite-drawer-item');
  if (!historyFavoriteItem) return;
  const hasAnyItem = hasDrawerRecentlyViewedItems() || hasDrawerWishlistItems();
  historyFavoriteItem.style.display = hasAnyItem ? '' : 'none';
}

const observer = new MutationObserver((mutations) => {
  const wishlistPage = document.querySelector('#wishlist-drawer-block wishlist-page');
  const containerBlock = document.querySelector('#wishlist-drawer-block');

  if (!wishlistPage || !containerBlock) return;

  const hasWishlistItems = hasDrawerWishlistItems();
  if (hasWishlistItems) {
    containerBlock.style.display = 'block';
  } else {
    containerBlock.style.display = 'none';
  }

  toggleHistoryFavoriteDrawerItem();
});

// <wishlist-page> 自体が存在したら、その中身の変化を監視開始
const targetNode = document.querySelector('#wishlist-drawer-block wishlist-page');
if (targetNode) {
  observer.observe(targetNode, { childList: true, subtree: true });
}
toggleHistoryFavoriteDrawerItem();

// 非同期描画が遅いケースの最終フォールバック
setTimeout(toggleHistoryFavoriteDrawerItem, 1200);

// ストア閲覧履歴保存
function trackRecentlyViewedStore() {
  if (!window.isBrandPage || !window.currentBrand) return;

  const storeData = {
    handle: window.currentBrand.handle,
    title: window.currentBrand.title,
    yomi: window.currentBrand.yomi,
    url: window.currentBrand.url,
    image: window.currentBrand.image
  };

  let stores = JSON.parse(localStorage.getItem('recentlyViewedStores') || '[]');

  stores = stores.filter(item => item.handle !== storeData.handle);
  stores.unshift(storeData);

  localStorage.setItem('recentlyViewedStores', JSON.stringify(stores));
}

document.addEventListener('DOMContentLoaded', trackRecentlyViewedStore);

// ストア閲覧履歴表示
class StoreRecentlyViewed extends HTMLElement {
  connectedCallback() {
    const container = this.querySelector('.store-history__wrap');
    const stores = JSON.parse(localStorage.getItem('recentlyViewedStores') || '[]');

    if (!container || stores.length === 0) return;

    const html = stores.map(store => `
      <li class="store-history__item">
        <a href="${store.url}">
          <div class="store-history__image">
            <img src="${store.image}" alt="${store.title}" loading="lazy" width="50" height="50">
          </div>
          <div class="store-history__info">
            <p class="store-history__title">${store.title}</p>
            <p class="store-history__yomi">${store.yomi}</p>
          </div>
        </a>
      </li>
    `).join('');

    container.innerHTML = html;
  }
}

if (!customElements.get('store-recently-viewed')) {
  customElements.define('store-recently-viewed', StoreRecentlyViewed);
}

/*
  おすすめ商品取得

  PDP の閲覧中商品をもとに /search の section rendering API を呼び出し、
  類似商品をグリッドに描画する。件数は Liquid の products_to_show を上限とし、
  足りない場合は filter を段階的に緩めて再取得する（tier fallback）。
*/
class RecommendedProductView extends HTMLElement {
  /**
   * search API の filter 定義（追加・削除はここだけ）
   * dataKey: getProductData() のキー / param: URL クエリ名
   * searchField: param の代わりに q へ `field:"値"` で入れる検索フィールド
   * required: tier 緩和で外さない条件（fetch 結果もクライアント側で一致を確認する）
   * getFilterTiers() は required 以外を配列末尾から1条件ずつ外した tier を生成する
   *
   * vendor を filter.p.vendor にしないのは、全件対象の q では Shopify の絞り込み候補に
   * 一部ブランド（Jil Sander 等）が含まれず、0件になるため。
   */
  static SEARCH_FILTER_RULES = [
    { dataKey: 'product_vendor', searchField: 'vendor', required: true, excludeSearchField: 'vendor', itemDatasetKey: 'productVendor' },
    { dataKey: 'product_available', param: 'filter.p.available' },
    { dataKey: 'product_type', param: 'filter.p.product_type', excludeSearchField: 'product_type', itemDatasetKey: 'productType' },
    { dataKey: 'product_size', param: 'filter.p.m.custom.relike_size', excludeMetafield: 'metafields.custom.relike_size', itemDatasetKey: 'productSize' },
    { dataKey: 'product_gender', param: 'filter.p.m.custom.relike_sex_type', excludeMetafield: 'metafields.custom.relike_sex_type', itemDatasetKey: 'productGender' },
    { dataKey: 'product_category', param: 'filter.p.m.custom.category', excludeMetafield: 'metafields.custom.category', itemDatasetKey: 'productCategory' },
    { dataKey: 'product_season', param: 'filter.p.m.custom.season', excludeMetafield: 'metafields.custom.season', itemDatasetKey: 'productSeason' },
    { dataKey: 'product_color', param: 'filter.p.m.custom.search_color_name', excludeMetafield: 'metafields.custom.search_color_name', itemDatasetKey: 'productColor' },
  ];

  /**
   * カスタム要素の接続時エントリポイント
   * PDP 以外（productId なし）では何もしない
   */
  connectedCallback() {
    if (!this.dataset.productId) return;
    void this.loadRecommendations();
  }

  /**
   * 表示件数の上限を取得
   * Liquid の data-products-to-show（section.settings.products_to_show）を参照
   * @returns {number}
   */
  getDisplayLimit() {
    const limit = parseInt(this.dataset.productsToShow, 10);
    return Number.isFinite(limit) && limit > 0 ? limit : 4;
  }

  /**
   * schema で有効化された filter ルール一覧を取得
   * data-recommend-filter-keys（カンマ区切り dataKey）が未設定の場合は全ルール
   * @returns {{ dataKey: string, param: string }[]}
   */
  getActiveFilterRules() {
    const keysRaw = this.dataset.recommendFilterKeys;
    if (!keysRaw || keysRaw.trim() === '') {
      return RecommendedProductView.SEARCH_FILTER_RULES;
    }

    const activeKeySet = new Set(
      keysRaw
        .split(',')
        .map((key) => key.trim())
        .filter(Boolean)
    );

    return RecommendedProductView.SEARCH_FILTER_RULES.filter((rule) => activeKeySet.has(rule.dataKey));
  }

  /**
   * schema で無効化（false）された filter ルール一覧を取得
   * data-recommend-exclude-keys（カンマ区切り dataKey）を参照
   * @returns {{ dataKey: string, param: string }[]}
   */
  getExcludeFilterRules() {
    const keysRaw = this.dataset.recommendExcludeKeys;
    if (!keysRaw || keysRaw.trim() === '') return [];

    const excludeKeySet = new Set(
      keysRaw
        .split(',')
        .map((key) => key.trim())
        .filter(Boolean)
    );

    return RecommendedProductView.SEARCH_FILTER_RULES.filter((rule) => excludeKeySet.has(rule.dataKey));
  }

  /**
   * search q パラメータ用に値をエスケープ
   * @param {string} value
   * @returns {string}
   */
  escapeSearchValue(value) {
    return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  }

  /**
   * exclude 条件を q パラメータへ追記
   * @param {string} query
   * @param {string} value
   * @param {{ excludeMetafield?: string, excludeSearchField?: string }} rule
   * @returns {string}
   */
  appendExcludeToQuery(query, value, rule) {
    if (!value) return query;

    const escaped = this.escapeSearchValue(value);
    if (rule.excludeMetafield) {
      return `${query} -${rule.excludeMetafield}:"${escaped}"`;
    }
    if (rule.excludeSearchField) {
      return `${query} -${rule.excludeSearchField}:"${escaped}"`;
    }
    return query;
  }

  /**
   * 一致条件を q パラメータへ追記
   * @param {string} query
   * @param {string} value
   * @param {string} field
   * @returns {string}
   */
  appendIncludeToQuery(query, value, field) {
    if (!value) return query;
    return `${query} ${field}:"${this.escapeSearchValue(value)}"`;
  }

  /**
   * required 条件がベース商品と一致する grid item かどうか
   * 検索側の絞り込みが効かなかった場合でも、条件違いの商品を表示しないためのガード
   * @param {HTMLElement} item
   * @param {Record<string, string>} productData
   * @returns {boolean}
   */
  matchesRequiredFilters(item, productData) {
    return this.getActiveFilterRules()
      .filter((rule) => rule.required && rule.itemDatasetKey && productData[rule.dataKey])
      .every((rule) => item.dataset[rule.itemDatasetKey] === String(productData[rule.dataKey]));
  }

  /**
   * 除外条件に一致する grid item かどうか（fetch 結果のクライアント側フォールバック）
   * @param {HTMLElement} item
   * @param {Record<string, string>} productData
   * @returns {boolean}
   */
  shouldExcludeItem(item, productData) {
    for (const rule of this.getExcludeFilterRules()) {
      const baseValue = productData[rule.dataKey];
      if (!baseValue || !rule.itemDatasetKey) continue;

      const itemValue = item.dataset[rule.itemDatasetKey];
      if (itemValue && String(itemValue) === String(baseValue)) {
        return true;
      }
    }
    return false;
  }


  /**
   * filter 緩和の tier 一覧を生成
   * tier0 = 全条件、tier1 = 末尾1条件除外、…、最終 tier = required 条件のみ
   * @returns {string[][]} 各 tier で有効にする dataKey の配列
   */
  getFilterTiers() {
    const rules = this.getActiveFilterRules();
    const requiredKeys = rules.filter((rule) => rule.required).map((rule) => rule.dataKey);
    const relaxableKeys = rules.filter((rule) => !rule.required).map((rule) => rule.dataKey);
    const tiers = [];
    for (let omitCount = 0; omitCount <= relaxableKeys.length; omitCount += 1) {
      tiers.push([...requiredKeys, ...relaxableKeys.slice(0, relaxableKeys.length - omitCount)]);
    }
    return tiers;
  }

  /**
   * 重複除外用 Set の初期値を作成
   * 閲覧中商品の item_code（なければ product_id）を登録し、
   * ベース商品と同品番のレコメンドが混ざらないようにする
   * @returns {Set<string>}
   */
  createSeenItemKeys() {
    const seen = new Set();
    const productData = this.getProductData();

    if (productData.product_item_code) {
      seen.add(String(productData.product_item_code));
    }
    if (productData.product_id) {
      seen.add(String(productData.product_id));
    }
    return seen;
  }

  /**
   * 商品 li 要素の dedupe キーを取得
   * item_code を優先し、未設定時は product id にフォールバック
   * @param {HTMLElement} item
   * @returns {string|undefined}
   */
  getItemKey(item) {
    return item.dataset.itemCode || item.dataset.pid;
  }

  /**
   * スライダー表示時に grid item へ必要な class / id を付与
   * @param {HTMLElement} item
   */
  prepareGridItem(item) {
    this.sanitizeGridItem(item);

    if (this.dataset.enableSliderLayout !== 'true') return;

    item.classList.add('slider__slide');
    if (!item.id) {
      const grid = this.getProductGrid();
      const slideIndex = grid ? grid.querySelectorAll('.grid__item').length + 1 : 1;
      item.id = `Slide-${this.dataset.id}-${slideIndex}`;
    }
  }

  /**
   * レコメンド取得〜描画のメインフロー
   * 1. tier ごとに section HTML を fetch
   * 2. item_code で dedupe しながら grid に追加
   * 3. 表示 limit に達するか tier 尽きるまで繰り返す
   */
  async loadRecommendations() {
    const productData = this.getProductData();
    const grid = this.getProductGrid();
    if (!grid) return;

    // product-fav-popup のように要素ごと DOM を移動されると connectedCallback が再実行され、
    // 読み込みが並行して同じ grid に追加される。最後に始まった読み込みだけを反映する。
    this.loadRunId = (this.loadRunId || 0) + 1;
    const runId = this.loadRunId;

    const limit = this.getDisplayLimit();
    const seenItemKeys = this.createSeenItemKeys();
    grid.innerHTML = '';

    for (const activeDataKeys of this.getFilterTiers()) {
      if (grid.children.length >= limit) break;

      const searchUrl = this.buildSearchUrl(productData, activeDataKeys);
      if (!searchUrl) continue;

      try {
        const response = await fetch(searchUrl);
        const sectionHtml = await response.text();
        if (runId !== this.loadRunId) return;
        const items = this.extractGridItems(sectionHtml);

        for (const item of items) {
          if (grid.children.length >= limit) break;

          const itemKey = this.getItemKey(item);
          if (!itemKey || seenItemKeys.has(itemKey)) continue;
          if (!this.matchesRequiredFilters(item, productData)) continue;
          if (this.shouldExcludeItem(item, productData)) continue;

          seenItemKeys.add(itemKey);
          const gridItem = document.importNode(item, true);
          this.prepareGridItem(gridItem);
          grid.appendChild(gridItem);
        }
      } catch (error) {
        console.warn('[recommended-product-view] fetch failed', error);
      }
    }
  }

  /**
   * data-product-* 属性を snake_case オブジェクトに変換
   * buildSearchUrl / createSeenItemKeys で参照するベース商品データ
   * @returns {Record<string, string>}
   */
  getProductData() {
    const data = {};
    Object.entries(this.dataset).forEach(([key, value]) => {
      if (!key.startsWith('product') || value === undefined || value === '') return;
      const snakeKey = key.replace(/([A-Z])/g, (char) => `_${char.toLowerCase()}`);
      data[snakeKey] = value;
    });
    return data;
  }

  /**
   * search API の q パラメータを生成
   * 閲覧中商品 ID を exclude し、同一商品が結果に含まれないようにする
   * @param {Record<string, string>} productData
   * @returns {string|null}
   */
  buildSearchQuery(productData) {
    const productId = productData.product_id;
    const itemCode = productData.product_item_code;
    if (!productId) return null;

    let q = `id:* OR exclude:${productId} `;

    if (itemCode) {
      q = this.appendExcludeToQuery(q, itemCode, { excludeMetafield: 'metafields.custom.item_code' });
    }

    // searchField を持つルールは required で全 tier 共通のため、tier に関係なく q に入れる
    for (const rule of this.getActiveFilterRules()) {
      if (rule.searchField) {
        q = this.appendIncludeToQuery(q, productData[rule.dataKey], rule.searchField);
      }
    }

    if (this.dataset.excludeSameSize === 'true' && productData.product_size) {
      q = this.appendExcludeToQuery(q, productData.product_size, {
        excludeMetafield: 'metafields.custom.relike_size',
      });
    }

    for (const rule of this.getExcludeFilterRules()) {
      q = this.appendExcludeToQuery(q, productData[rule.dataKey], rule);
    }

    return q;
  }

  /**
   * section rendering API の fetch URL を組み立て
   * @param {Record<string, string>} productData ベース商品データ
   * @param {string[]|null} activeDataKeys この tier で適用する filter の dataKey 一覧（null = 全適用）
   * @returns {string|null}
   */
  buildSearchUrl(productData, activeDataKeys = null) {
    if (!productData || typeof productData !== 'object') return null;
    if (!this.dataset.url || !this.dataset.id) return null;

    const q = this.buildSearchQuery(productData);
    if (!q) return null;

    const activeFilterRules = this.getActiveFilterRules();
    const activeKeys = activeDataKeys ?? activeFilterRules.map((rule) => rule.dataKey);
    const activeKeySet = new Set(activeKeys);

    const params = new URLSearchParams();
    params.set('section_id', this.dataset.id);
    params.set('type', 'product');
    params.set('q', q);

    activeFilterRules.forEach(({ dataKey, param }) => {
      if (!activeKeySet.has(dataKey)) return;
      if (!param) return;
      const value = productData[dataKey];
      if (dataKey === 'product_available') {
        params.set('filter.v.availability', '1');
        return;
      }
      if (value) params.set(param, value);
    });

    return `${this.dataset.url}?${params.toString()}`;
  }

  /**
   * 描画先の商品グリッド ul 要素を取得
   * @returns {HTMLElement|null}
   */
  getProductGrid() {
    return this.querySelector('.recommended-product-grid');
  }

  /**
   * fetch した section HTML から商品 li 要素の配列を抽出
   * Liquid 側の item_code dedupe 済み HTML をパースする
   * @param {string} sectionHtml
   * @returns {HTMLElement[]}
   */
  sanitizeGridItem(item) {
    item.querySelectorAll('style, link[rel="stylesheet"]').forEach((node) => node.remove());
  }

  extractGridItems(sectionHtml) {
    const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const grid =
      doc.querySelector('.recommended-product-grid--api') || doc.querySelector('.recommended-product-grid');
    if (!grid) return [];
    const items = [...grid.querySelectorAll('.grid__item')];
    items.forEach((item) => this.sanitizeGridItem(item));
    return items;
  }
}
if (!customElements.get('recommended-product-view')) {
  customElements.define('recommended-product-view', RecommendedProductView);
}